import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { packageSealView, sealView, type SealTone } from "./format";
import type { ExpiredMotive, PackageRegistration, SlaPause, VacancyRegistration } from "./types";

/** Pílula do mockup V1 (kit.js `chip`: 20 px, 11 px, peso 600). */
const PILL = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  height: 20,
  px: 0.875,
  borderRadius: "10px",
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: "nowrap",
  maxWidth: "100%",
  "& svg": { fontSize: 13 },
} as const;

const Dash = () => (
  <Box component="span" sx={{ color: "text.secondary" }}>
    —
  </Box>
);

// ---------------- canal ----------------
type ChannelKey = "form" | "me_manual" | "me_auto" | "minha_escala" | "meu_plantao" | "mixed";
const CHANNEL: Record<ChannelKey, { short: string; long: string; bg: string; fg: string }> = {
  form: { short: "Formulário", long: "Formulário do site", bg: "#eef2f6", fg: "#17384c" },
  me_manual: { short: "ME manual", long: "Minha Escala · solicitação manual", bg: "#e0f6fc", fg: "#026a87" },
  me_auto: { short: "ME automático", long: "Minha Escala · solicitação recorrente (automática)", bg: "#e0f4f1", fg: "#1b7a71" },
  minha_escala: { short: "Minha Escala", long: "Minha Escala · tipo não identificado", bg: "#eef2f6", fg: "#475569" },
  meu_plantao: { short: "Meu Plantão", long: "Meu Plantão (legado)", bg: "#eef2f6", fg: "#556274" },
  mixed: { short: "Misto", long: "Plantões de mais de um canal", bg: "#f1ecfb", fg: "#5b3fb0" },
};

/** Chip de canal (rótulo curto; o nome completo fica na dica). Canal fora da lista = "—". */
export function ChannelChip({ channel }: { channel: string | null | undefined }) {
  const c = channel && channel in CHANNEL ? CHANNEL[channel as ChannelKey] : null;
  if (!c) return <Dash />;
  return (
    <Tooltip title={c.long} describeChild>
      <Box component="span" tabIndex={0} aria-label={`Canal: ${c.long}`} data-testid="channel-chip" sx={{ ...PILL, bgcolor: c.bg, color: c.fg }}>
        {c.short}
      </Box>
    </Tooltip>
  );
}

// ---------------- selo de registro ----------------
const SEAL: Record<SealTone, { bg: string; fg: string; icon: ReactNode }> = {
  ok: { bg: "#e7f5ea", fg: "#1b5e20", icon: <TaskAltIcon aria-hidden /> },
  pending: { bg: "#fff4e0", fg: "#8a5300", icon: <PendingActionsOutlinedIcon aria-hidden /> },
  audit: { bg: "#ede7f6", fg: "#4527a0", icon: <PolicyOutlinedIcon aria-hidden /> },
  noPolicy: { bg: "#eceff1", fg: "#546e7a", icon: <BlockOutlinedIcon aria-hidden /> },
  unknown: { bg: "#eceff1", fg: "#546e7a", icon: <HelpOutlineIcon aria-hidden /> },
};

const HINT: Record<SealTone, string> = {
  ok: "Registro do plantão aprovado no Minha Escala.",
  pending: "Plantão sem registro aprovado no Minha Escala.",
  audit: "Registro enviado, aguardando a auditoria no Minha Escala.",
  noPolicy: "A escala não exige registro neste plantão: a empresa confere pela folha.",
  unknown: "Sem dado do Minha Escala agora (não bloqueia nenhuma ação).",
};

/** `plain`: sem dica e sem foco próprio — para usar dentro de um botão (cartão do Quadro). */
function Seal({ tone, label, title, ariaLabel, testId, plain = false }: { tone: SealTone; label: string; title: ReactNode; ariaLabel: string; testId: string; plain?: boolean }) {
  const s = SEAL[tone];
  const chip = (
    <Box component="span" tabIndex={plain ? undefined : 0} aria-label={plain ? undefined : ariaLabel} data-testid={testId} data-tone={tone} sx={{ ...PILL, bgcolor: s.bg, color: s.fg }}>
      {s.icon}
      {label}
    </Box>
  );
  if (plain) return chip;
  return (
    <Tooltip title={title} describeChild>
      {chip}
    </Tooltip>
  );
}

/** Selo de UM plantão (OK / pendente / auditoria / sem política / "—"); a dica traz o motivo dado pelo ME. */
export function RegistrationSealChip({ registration }: { registration: Pick<VacancyRegistration, "status"> & Partial<VacancyRegistration> | null | undefined }) {
  const { tone, label } = sealView(registration?.status);
  const hint = registration?.reasonLabel || HINT[tone];
  return <Seal tone={tone} label={label} title={hint} ariaLabel={`${label}: ${hint}`} testId="registration-seal" />;
}

const BRT = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
export const brtShort = (iso: string | null | undefined): string | null => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? BRT.format(t).replace(",", "") : null;
};

/** Selo agregado do pacote: "3/5 com registro OK" no tom do pior estado. `null` (sem plantão do ME) = "—". */
export function PackageSealChip({ registration, plain = false }: { registration: PackageRegistration | null | undefined; plain?: boolean }) {
  const view = packageSealView(registration);
  if (!view) return <Dash />;
  const at = brtShort(registration?.fetchedAt);
  const title = (
    <>
      Pior estado entre os plantões do pacote.
      {at && <br />}
      {at && `Fonte: Minha Escala, atualizado em ${at}.`}
    </>
  );
  return <Seal tone={view.tone} label={view.label} title={title} ariaLabel={`Registro no ME: ${view.label}`} testId="package-seal" plain={plain} />;
}

// ---------------- pausa do SLA e motivo de sistema ----------------
/** "SLA da empresa pausado" (kit.js `relogioParado`). */
export function SlaPausedChip({ pause, plain = false }: { pause: SlaPause; plain?: boolean }) {
  const since = brtShort(pause.since);
  const chip = (
    <Box
      component="span"
      tabIndex={plain ? undefined : 0}
      aria-label={plain ? undefined : "SLA da empresa pausado: registro pendente no Minha Escala"}
      data-testid="sla-paused"
      sx={{ ...PILL, bgcolor: "#eceff1", color: "#546e7a" }}>
      <PauseCircleOutlineIcon aria-hidden />
      SLA da empresa pausado
    </Box>
  );
  if (plain) return chip;
  return (
    <Tooltip
      describeChild
      title={
        <>
          Motivo de sistema: registro pendente no Minha Escala. O tempo parado não conta contra a empresa.
          {since && <br />}
          {since && `Pausado desde ${since}.`}
        </>
      }
    >
      {chip}
    </Tooltip>
  );
}

/** Chip tracejado de motivo de sistema (kit.js `motivoSistema`). */
export function SystemReasonChip({ label = "Registro pendente no ME" }: { label?: string }) {
  return (
    <Box component="span" data-testid="system-reason" sx={{ ...PILL, bgcolor: "#f1f5f9", color: "#334155", border: "1px dashed #94a3b8" }}>
      <HourglassBottomOutlinedIcon aria-hidden />
      {label}
    </Box>
  );
}

const MOTIVE_FALLBACK: Record<ExpiredMotive["code"], string> = {
  me_registration_pending: "Sistema · registro pendente no ME",
  company_no_review: "Empresa · sem conferência",
  no_policy_no_review: "Empresa · escala sem política, sem conferência",
};

/** Motivo do expirado (§2.3): sistema × empresa. */
export function ExpiredMotiveChip({ motive, compact = false }: { motive: ExpiredMotive | null | undefined; compact?: boolean }) {
  if (!motive || (motive.kind !== "system" && motive.kind !== "company")) return <Dash />;
  const label = motive.label || MOTIVE_FALLBACK[motive.code] || "—";
  if (motive.kind === "system") {
    if (compact) return <SystemReasonChip label={label} />;
    return (
      <Box data-testid="expired-motive" data-kind="system" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: 0.25 }}>
        <SystemReasonChip label={label} />
        <Box component="span" sx={{ fontSize: 10.5, color: "text.secondary" }}>
          não conta contra a empresa
        </Box>
      </Box>
    );
  }
  // Mockup V1 (Fila, slaCell 'exp'): motivo da empresa em texto cinza, sem chip.
  return (
    <Box component="span" data-testid="expired-motive" data-kind="company" sx={{ fontSize: 11, color: "#64748b", bgcolor: "transparent" }}>
      {label}
    </Box>
  );
}
